import { Box, IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material"
import React, { useEffect } from "react"

import { Pool } from "../../api/pools-api"
import { measureLatency } from "../../api/process-api"
import { AreaChart } from "../../components/Charts/AreaChart"
import { HighchartAreaData } from "../../types"
import ENV from "@/env"

export function PoolPing(props: { pool?: Pool }) {
  const { pool } = props

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [poolLatencies, setPoolLatencies] = React.useState<HighchartAreaData[]>([])
  const [dexiLatencies, setDexiLatencies] = React.useState<HighchartAreaData[]>([])
  const [baseTokenLatencies, setBaseTokenLatencies] = React.useState<HighchartAreaData[]>([])
  const [quoteTokenLatencies, setQuoteTokenLatencies] = React.useState<HighchartAreaData[]>([])

  useEffect(() => {
    if (!pool || anchorEl === null) return
    function fetch() {
      if (!pool || anchorEl === null) return
      Promise.all([
        measureLatency(pool.id),
        measureLatency(ENV.VITE_DEXI_PROCESS),
        measureLatency(pool.baseToken),
        measureLatency(pool.quoteToken),
      ]).then(([poolLatency, dexiLatency, baseTokenLatency, quoteTokenLatency]) => {
        setPoolLatencies((prev) => [...prev.slice(-29), poolLatency])
        setDexiLatencies((prev) => [...prev.slice(-29), dexiLatency])
        setBaseTokenLatencies((prev) => [...prev.slice(-29), baseTokenLatency])
        setQuoteTokenLatencies((prev) => [...prev.slice(-29), quoteTokenLatency])
      })
    }

    fetch()
    const interval = setInterval(() => {
      fetch()
    }, 2000)

    return () => clearInterval(interval)
  }, [pool, anchorEl])

  return (
    <>
      <Tooltip title="Connection Latency">
        <IconButton onClick={handleClick}>
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#44b700",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: "#44b700",
                animation: "ripple 1.2s infinite ease-in-out",
              },
              "@keyframes ripple": {
                "0%": {
                  transform: "scale(.8)",
                  opacity: 1,
                },
                "100%": {
                  transform: "scale(2.4)",
                  opacity: 0,
                },
              },
            }}
          />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack
          sx={{
            width: 300,
            paddingY: 2,
            paddingX: 1,
            "& > .MuiTypography-root": {
              paddingX: 1,
            },
          }}
          gap={1}
        >
          <Typography variant="subtitle1">Connection latency</Typography>
          <Typography variant="caption">Pool</Typography>
          <AreaChart data={poolLatencies} />
          <Typography variant="caption">Dexi</Typography>
          <AreaChart data={dexiLatencies} />
          <Typography variant="caption">Base token</Typography>
          <AreaChart data={baseTokenLatencies} />
          <Typography variant="caption">Quote token</Typography>
          <AreaChart data={quoteTokenLatencies} />
        </Stack>
      </Popover>
    </>
  )
}
